import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Package as PackageIcon, Minus, Plus, MessageCircle } from "lucide-react";
import { fetchProducts, validatePlayer, createCheckout } from "../lib/api";
import { useAuth } from "../lib/auth";
import PaymentQRDialog from "./PaymentQRDialog";
import ManualTransferCheckout from "./ManualTransferCheckout";
import ChytaPayDialog from "./ChytaPayDialog";
import CategoryTabs from "./CategoryTabs";
import { fmtARS } from "../lib/utils-app";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function ProductCheckout({
  category,
  theme, // { primary, primaryHover, ring, bg, accent, text }
  title,
  subtitle,
  bulletTag,
  ProductCard,
  extraCategory,
}) {
  const { user } = useAuth();
  const [productsData, setProductsData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [playerId, setPlayerId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [validating, setValidating] = useState(false);
  const [valid, setValid] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [qrOrder, setQrOrder] = useState(null);
  const [payOrder, setPayOrder] = useState(null);

  useEffect(() => {
    fetchProducts()
      .then(setProductsData)
      .catch(() => toast.error("No se pudieron cargar los productos"));
  }, []);

  const activeCategory = selected?.category || category;
  const cat = productsData?.categories?.[activeCategory];
  const items = [
    ...(productsData?.products?.[category] || []),
    ...(extraCategory ? productsData?.products?.[extraCategory] || [] : []),
  ];
  const quantitySelectable = cat?.quantity_selectable;
  const minQty = selected?.min_qty || 1;
  const MAX_QTY = 1000;

  const total = useMemo(() => {
    if (!selected) return 0;
    return selected.unit_price_ars * (quantitySelectable ? quantity : 1);
  }, [selected, quantity, quantitySelectable]);

  const canCheckout = useMemo(
    () => !!selected && !!playerId && valid?.valid === true && !processing,
    [selected, playerId, valid, processing]
  );

  async function handleValidate() {
    if (!playerId) return;
    setValidating(true);
    try {
      const res = await validatePlayer(playerId, activeCategory);
      setValid(res);
      if (res.valid) toast.success(res.nickname ? `Jugador: ${res.nickname}` : "Identificador válido");
      else toast.error(res.error || "Identificador inválido");
    } catch {
      toast.error("Error validando");
    } finally {
      setValidating(false);
    }
  }

  async function handleCheckout() {
    if (!user) {
      toast.info("Iniciá sesión con Google para continuar");
      startGoogleLogin();
      return;
    }
    if (!canCheckout) return;
    setProcessing(true);
    try {
      const res = await createCheckout({
        package_id: selected.id,
        player_id: playerId,
        player_nickname: valid?.nickname || null,
        quantity: quantitySelectable ? quantity : 1,
      });
      if (res.provider === "chytapay") {
        setPayOrder(res);
      } else if (res.checkout_url) {
        setQrOrder({
          order_id: res.order_id,
          checkout_url: res.checkout_url,
          amount_ars: res.amount_ars,
          diamonds: selected.diamonds,
          robux: selected.robux,
          product_name: selected.name,
          quantity: quantitySelectable ? quantity : 1,
          player_id: playerId,
          player_nickname: valid?.nickname || null,
          status: "pending_payment",
          category,
          extra_instructions: res.extra_instructions,
        });
      } else {
        toast.error("No se pudo iniciar el pago");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error creando el pago";
      toast.error(String(msg));
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div className="min-h-screen" style={{ background: theme.bg }}>
      <PaymentQRDialog
        open={!!qrOrder}
        onClose={() => setQrOrder(null)}
        order={qrOrder}
        themeColor={theme.primary}
      />
      <ChytaPayDialog order={payOrder} onClose={() => setPayOrder(null)} />

      <CategoryTabs active={category} />

      {/* Hero */}
      <section className="relative overflow-hidden pt-14 sm:pt-20 pb-8 sm:pb-12">
        <div
          className="absolute -top-24 right-0 sm:-right-24 w-64 h-64 sm:w-[420px] sm:h-[420px] rounded-full blur-3xl pointer-events-none"
          style={{ background: `${theme.primary}33` }}
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs tracking-widest mb-5 border"
            style={{
              background: `${theme.primary}1a`,
              borderColor: `${theme.primary}66`,
              color: theme.primary,
            }}
          >
            <PackageIcon className="w-3.5 h-3.5" /> {bulletTag}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-wider text-white">
            {title}
          </h1>
          <p className="mt-4 text-neutral-300 text-base sm:text-lg max-w-2xl">{subtitle}</p>
          {cat?.extra_instructions && (
            <div
              className="mt-6 rounded-lg border p-4 text-sm flex items-start gap-3 max-w-2xl"
              style={{
                background: "#0d0d0d",
                borderColor: `${theme.primary}55`,
                color: "#e5e5e5",
              }}
            >
              <MessageCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: theme.primary }} />
              <div>
                <div className="font-semibold text-white mb-0.5">Cómo se acredita</div>
                {cat.extra_instructions}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PASO 1 */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="text-xs tracking-[0.3em]" style={{ color: theme.primary }}>PASO 1</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-wider text-white mb-4">
          Ingresá tu {cat?.identifier_label || "Identificador"}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          <div>
            <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
              {cat?.identifier_label} ({cat?.identifier_hint})
            </label>
            <div className="flex gap-2">
              <input
                data-testid="player-id-input"
                type="text"
                value={playerId}
                onChange={(e) => {
                  setPlayerId(e.target.value.slice(0, 25));
                  setValid(null);
                }}
                placeholder={cat?.identifier_placeholder || ""}
                className="flex-1 min-w-0 px-4 py-3 rounded-md text-base tracking-wider bg-black border text-white placeholder:text-neutral-600 focus:outline-none"
                style={{
                  borderColor: `${theme.primary}55`,
                  boxShadow: valid?.valid ? `0 0 0 2px ${theme.primary}55` : "none",
                }}
              />
              <button
                data-testid="validate-id-btn"
                onClick={handleValidate}
                disabled={!playerId || validating}
                className="px-5 py-3 rounded-md text-sm font-bold text-black"
                style={{
                  background: theme.primary,
                  opacity: !playerId || validating ? 0.5 : 1,
                  transition: "background-color 180ms ease",
                }}
                onMouseEnter={(e) => { if (playerId && !validating) e.currentTarget.style.background = theme.primaryHover; }}
                onMouseLeave={(e) => (e.currentTarget.style.background = theme.primary)}
              >
                {validating ? "Validando..." : "Validar"}
              </button>
            </div>
            {valid && (
              <div className={`mt-2 text-sm flex items-center gap-1.5 ${valid.valid ? "text-emerald-400" : "text-rose-400"}`}>
                {valid.valid ? <CheckCircle2 className="w-4 h-4" /> : null}
                {valid.valid ? `Identificador válido${valid.nickname ? ` · ${valid.nickname}` : ""}` : valid.error}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-white/5" />
      </div>

      {/* PASO 2 - Products */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em]" style={{ color: theme.primary }}>PASO 2</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-wider text-white">
              Elegí tu {quantitySelectable ? "producto" : "paquete"}
            </h2>
          </div>
          <div className="hidden sm:block text-sm text-neutral-400">
            {items.length} disponibles
          </div>
        </div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
          className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
        >
          {items.map((p) => (
            <motion.div
              key={p.id}
              variants={{
                hidden: { opacity: 0, y: 16 },
                show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
              }}
            >
              <ProductCard
                product={p}
                selected={selected?.id === p.id}
                onSelect={(prod) => {
                  setSelected(prod);
                  setQuantity(prod.min_qty || 1);
                }}
                theme={theme}
              />
            </motion.div>
          ))}
        </motion.div>

        {quantitySelectable && selected && (
          <div className="mt-6 max-w-md rounded-xl border p-4" style={{ background: "#0d0d0d", borderColor: `${theme.primary}55` }}>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
              Cantidad de {selected.name}
              {minQty > 1 && <span className="ml-2 normal-case tracking-normal" style={{ color: theme.primary }} data-testid="qty-min-hint">(mínimo {minQty})</span>}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity((q) => Math.max(minQty, q - 1))}
                data-testid="qty-minus"
                className="w-10 h-10 rounded-md grid place-items-center border"
                style={{ borderColor: `${theme.primary}55` }}
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                data-testid="qty-input"
                type="number"
                min={minQty}
                max={MAX_QTY}
                value={quantity}
                onChange={(e) => {
                  const n = parseInt(e.target.value || "0", 10);
                  setQuantity(Math.min(MAX_QTY, isNaN(n) ? minQty : n));
                }}
                onBlur={() => setQuantity((q) => Math.max(minQty, q))}
                className="w-20 h-10 rounded-md bg-black border text-center text-white"
                style={{ borderColor: `${theme.primary}55` }}
              />
              <button
                onClick={() => setQuantity((q) => Math.min(MAX_QTY, q + 1))}
                data-testid="qty-plus"
                className="w-10 h-10 rounded-md grid place-items-center border"
                style={{ borderColor: `${theme.primary}55` }}
              >
                <Plus className="w-4 h-4" />
              </button>
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Subtotal</div>
                <div className="font-bold text-white">{fmtARS(total)}</div>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-white/5" />
      </div>

      {/* PASO 3 - Checkout */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24">
        <div className="text-xs tracking-[0.3em]" style={{ color: theme.primary }}>PASO 3</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-wider text-white mb-4">Confirmá y pagá</h2>

        <div className="rounded-xl p-5 sm:p-6 max-w-3xl" style={{ background: "#0d0d0d", border: `1px solid ${theme.primary}55` }}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-neutral-500">Producto</div>
              <div className="font-display text-2xl tracking-wider text-white truncate">
                {selected ? (quantitySelectable && quantity > 1 ? `${quantity} × ${selected.name}` : selected.name) : "—"}
              </div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-white/10" />
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">{cat?.identifier_label}</div>
              <div className="font-mono text-lg text-white">{playerId || "—"}</div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-white/10" />
            <div className="sm:ml-auto">
              <div className="text-xs uppercase tracking-widest text-neutral-500">Total</div>
              <div className="font-display text-3xl tracking-wider" style={{ color: theme.primary }}>
                {selected ? fmtARS(total) : "—"}
              </div>
            </div>
          </div>

          {cat?.manual_payment ? (
            <div className="mt-5">
              <ManualTransferCheckout
                alias={cat.manual_payment.alias}
                selected={selected}
                quantity={quantitySelectable ? quantity : 1}
                playerId={playerId}
                nickname={valid?.nickname || null}
                total={total}
                canSubmit={canCheckout && (!quantitySelectable || quantity >= minQty)}
                categoryLabel={cat.label}
                identifierLabel={cat.identifier_label}
                user={user}
                onLogin={() => { toast.info("Iniciá sesión con Google para continuar"); startGoogleLogin(); }}
                theme={theme}
              />
            </div>
          ) : (
          <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <button
              data-testid="checkout-btn"
              onClick={handleCheckout}
              disabled={!canCheckout}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-md text-base font-bold text-black"
              style={{
                background: theme.primary,
                opacity: !canCheckout ? 0.5 : 1,
                transition: "background-color 180ms ease, box-shadow 220ms ease",
              }}
              onMouseEnter={(e) => { if (canCheckout) { e.currentTarget.style.background = theme.primaryHover; e.currentTarget.style.boxShadow = `0 0 24px ${theme.primary}88`; } }}
              onMouseLeave={(e) => { e.currentTarget.style.background = theme.primary; e.currentTarget.style.boxShadow = "none"; }}
            >
              <CreditCard className="w-5 h-5" />
              {user ? (processing ? "Generando cobro..." : "Pagar por transferencia") : "Iniciar sesión y pagar"}
            </button>
            <div className="text-xs text-neutral-400 sm:max-w-[240px]">
              Vas a ver un <span className="text-white">CVU</span> y el monto exacto. Transferí desde cualquier banco o billetera y se acredita solo.
              {cat?.extra_instructions && (
                <div className="mt-1 text-neutral-500">Acreditado el pago, procesamos tu carga.</div>
              )}
            </div>
          </div>
          )}
        </div>
      </section>
    </div>
  );
}
